import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { Loader2, Save, Mail, Trash2, Plus, BookOpen, Users, Copy, Check, Truck, Megaphone, ImageIcon, Sparkles, Gift } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fixingImages, setFixingImages] = useState(false);
  const [settings, setSettings] = useState({
    store_name: '',
    email: '',
    phone: '',
    address: '',
    about_text: '',
    about_story: '',
    shipping_rate: 3.99,
    free_shipping_threshold: 50.00,
    shipping_enabled: true,
    promo_banner_enabled: true,
    promo_banner_text: '',
    promo_banner_link: '',
    promo_banner_bg_color: '#8B4513',
    newsletter_popup_enabled: true,
    newsletter_popup_title: '',
    newsletter_popup_text: '',
    newsletter_discount_code: ''
  });
  const [messages, setMessages] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [bookSubcategories, setBookSubcategories] = useState([]);
  const [newSubcategory, setNewSubcategory] = useState('');
  const [adminInvites, setAdminInvites] = useState([]);
  const [newInviteEmail, setNewInviteEmail] = useState('');
  const [copiedCode, setCopiedCode] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [settingsRes, messagesRes, subscribersRes, subcatRes, invitesRes] = await Promise.all([
        axios.get(`${API}/settings`),
        axios.get(`${API}/admin/messages`),
        axios.get(`${API}/admin/subscribers`),
        axios.get(`${API}/book-subcategories`),
        axios.get(`${API}/admin/invites`)
      ]);
      setSettings(settingsRes.data);
      setMessages(messagesRes.data);
      setSubscribers(subscribersRes.data);
      setBookSubcategories(subcatRes.data);
      setAdminInvites(invitesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSettings((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, settings);
      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    try {
      await axios.delete(`${API}/admin/messages/${messageId}`);
      toast.success('Message deleted');
      setMessages(messages.filter((m) => m.id !== messageId));
    } catch (error) {
      toast.error('Failed to delete message');
    }
  };

  const handleDeleteSubscriber = async (subscriberId) => {
    try {
      await axios.delete(`${API}/admin/subscribers/${subscriberId}`);
      toast.success('Subscriber removed');
      setSubscribers(subscribers.filter((s) => s.id !== subscriberId));
    } catch (error) {
      toast.error('Failed to remove subscriber');
    }
  };

  const handleAddSubcategory = async () => {
    if (!newSubcategory.trim()) {
      toast.error('Please enter a subcategory name');
      return;
    }
    try {
      const response = await axios.post(`${API}/admin/book-subcategories`, { name: newSubcategory });
      setBookSubcategories([...bookSubcategories, response.data]);
      setNewSubcategory('');
      toast.success('Subcategory added');
    } catch (error) {
      toast.error('Failed to add subcategory');
    }
  };

  const handleDeleteSubcategory = async (subcatId) => {
    try {
      await axios.delete(`${API}/admin/book-subcategories/${subcatId}`);
      setBookSubcategories(bookSubcategories.filter((s) => s.id !== subcatId));
      toast.success('Subcategory deleted');
    } catch (error) {
      toast.error('Failed to delete subcategory');
    }
  };

  const handleCreateInvite = async () => {
    if (!newInviteEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }
    try {
      const response = await axios.post(`${API}/admin/invites`, { email: newInviteEmail });
      setAdminInvites([response.data, ...adminInvites]);
      setNewInviteEmail('');
      toast.success('Invite created! Share the code with the new admin.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invite');
    }
  };

  const handleDeleteInvite = async (inviteId) => {
    try {
      await axios.delete(`${API}/admin/invites/${inviteId}`);
      setAdminInvites(adminInvites.filter((i) => i.id !== inviteId));
      toast.success('Invite deleted');
    } catch (error) {
      toast.error('Failed to delete invite');
    }
  };

  const copyInviteCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    toast.success('Invite code copied to clipboard');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const handleFixBrokenImages = async () => {
    setFixingImages(true);
    try {
      const response = await axios.post(`${API}/admin/fix-broken-images`);
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Failed to fix images');
    } finally {
      setFixingImages(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div data-testid="admin-settings-page">
      <h2 className="font-serif text-xl font-bold mb-6">Store Settings</h2>

      <Tabs defaultValue="general" className="space-y-6">
        <TabsList className="flex-wrap">
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="promotions">Promotions</TabsTrigger>
          <TabsTrigger value="images">Image Tools</TabsTrigger>
          <TabsTrigger value="shipping">Shipping</TabsTrigger>
          <TabsTrigger value="books">Book Categories</TabsTrigger>
          <TabsTrigger value="admins">Admin Users</TabsTrigger>
          <TabsTrigger value="subscribers">Subscribers ({subscribers.length})</TabsTrigger>
        </TabsList>

        {/* General Settings */}
        <TabsContent value="general">
          <Card>
            <CardHeader>
              <CardTitle>Store Information</CardTitle>
              <CardDescription>Update your store&apos;s contact information and about text</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="store_name">Store Name</Label>
                  <Input
                    id="store_name"
                    name="store_name"
                    value={settings.store_name}
                    onChange={handleInputChange}
                    placeholder="Brookzone Bookseller"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={settings.email}
                    onChange={handleInputChange}
                    placeholder="hello@brookzone.com"
                  />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    name="phone"
                    value={settings.phone}
                    onChange={handleInputChange}
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="address">Address</Label>
                  <Input
                    id="address"
                    name="address"
                    value={settings.address}
                    onChange={handleInputChange}
                    placeholder="123 Main Street"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="about_text">About Text (Short)</Label>
                <textarea
                  id="about_text"
                  name="about_text"
                  value={settings.about_text}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="Brief description for footer and headers"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="about_story">About Story (Full)</Label>
                <textarea
                  id="about_story"
                  name="about_story"
                  value={settings.about_story}
                  onChange={handleInputChange}
                  rows={5}
                  className="w-full px-3 py-2 border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="Full story for the About page"
                />
              </div>
              <Button onClick={handleSaveSettings} className="btn-primary" disabled={saving}>
                {saving ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Saving...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Save className="h-4 w-4" />
                    Save Settings
                  </span>
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Promotions Settings */}
        <TabsContent value="promotions">
          <div className="space-y-6">
            {/* Promotional Banner */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Megaphone className="h-5 w-5" />
                  Promotional Banner
                </CardTitle>
                <CardDescription>
                  Display a promotional banner at the top of your store
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                  <div>
                    <Label className="text-base font-medium">Enable Banner</Label>
                    <p className="text-sm text-muted-foreground">Show promotional banner to visitors</p>
                  </div>
                  <Switch
                    checked={settings.promo_banner_enabled}
                    onCheckedChange={(checked) => setSettings(prev => ({ ...prev, promo_banner_enabled: checked }))}
                  />
                </div>
                
                {settings.promo_banner_enabled && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="promo_banner_text">Banner Text</Label>
                      <Input
                        id="promo_banner_text"
                        value={settings.promo_banner_text}
                        onChange={(e) => setSettings(prev => ({ ...prev, promo_banner_text: e.target.value }))}
                        placeholder="Free shipping on orders over $50!"
                      />
                    </div>
                    
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="promo_banner_link">Link URL</Label>
                        <Input
                          id="promo_banner_link"
                          value={settings.promo_banner_link}
                          onChange={(e) => setSettings(prev => ({ ...prev, promo_banner_link: e.target.value }))}
                          placeholder="/products"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="promo_banner_bg_color">Background Color</Label>
                        <div className="flex gap-2">
                          <Input
                            id="promo_banner_bg_color"
                            type="color"
                            value={settings.promo_banner_bg_color}
                            onChange={(e) => setSettings(prev => ({ ...prev, promo_banner_bg_color: e.target.value }))}
                            className="w-16 h-10 p-1 cursor-pointer"
                          />
                          <Input
                            value={settings.promo_banner_bg_color}
                            onChange={(e) => setSettings(prev => ({ ...prev, promo_banner_bg_color: e.target.value }))}
                            placeholder="#8B4513"
                            className="flex-1"
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* Preview */}
                    <div className="space-y-2">
                      <Label>Preview</Label>
                      <div 
                        className="py-2.5 px-4 text-center text-white text-sm font-medium rounded-lg"
                        style={{ backgroundColor: settings.promo_banner_bg_color || '#8B4513' }}
                      >
                        {settings.promo_banner_text || 'Your promotional message here'}
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Newsletter Popup */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Gift className="h-5 w-5" />
                  Newsletter Popup
                </CardTitle>
                <CardDescription>
                  Capture emails with an offer popup for first-time visitors
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                  <div>
                    <Label className="text-base font-medium">Enable Newsletter Popup</Label>
                    <p className="text-sm text-muted-foreground">Show popup to new visitors after 5 seconds</p>
                  </div>
                  <Switch
                    checked={settings.newsletter_popup_enabled}
                    onCheckedChange={(checked) => setSettings(prev => ({ ...prev, newsletter_popup_enabled: checked }))}
                  />
                </div>
                
                {settings.newsletter_popup_enabled && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="newsletter_popup_title">Popup Title</Label>
                      <Input
                        id="newsletter_popup_title"
                        value={settings.newsletter_popup_title}
                        onChange={(e) => setSettings(prev => ({ ...prev, newsletter_popup_title: e.target.value }))}
                        placeholder="Get 10% Off Your First Order!"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="newsletter_popup_text">Popup Description</Label>
                      <Textarea
                        id="newsletter_popup_text"
                        value={settings.newsletter_popup_text}
                        onChange={(e) => setSettings(prev => ({ ...prev, newsletter_popup_text: e.target.value }))}
                        placeholder="Subscribe to our newsletter for exclusive deals and new arrivals."
                        rows={2}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="newsletter_discount_code">Discount Code</Label>
                      <Input
                        id="newsletter_discount_code"
                        value={settings.newsletter_discount_code}
                        onChange={(e) => setSettings(prev => ({ ...prev, newsletter_discount_code: e.target.value.toUpperCase() }))}
                        placeholder="WELCOME10"
                        className="uppercase font-mono"
                      />
                      <p className="text-xs text-muted-foreground">This code will be shown after subscription</p>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            <Button onClick={handleSaveSettings} className="btn-primary" disabled={saving}>
              {saving ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Save className="h-4 w-4" />
                  Save Promotion Settings
                </span>
              )}
            </Button>
          </div>
        </TabsContent>

        {/* Image Tools */}
        <TabsContent value="images">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ImageIcon className="h-5 w-5" />
                Image Tools
              </CardTitle>
              <CardDescription>
                Fix broken product images and manage visual content
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-muted/50 p-6 rounded-lg space-y-4">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-primary/10 rounded-lg">
                    <Sparkles className="h-8 w-8 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-lg">Fix Broken Images</h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      Automatically replace broken local upload URLs with high-quality stock images 
                      based on product categories. This is useful after deployment when local 
                      uploads don&apos;t persist.
                    </p>
                  </div>
                </div>
                <Button 
                  onClick={handleFixBrokenImages} 
                  className="btn-accent w-full sm:w-auto"
                  disabled={fixingImages}
                >
                  {fixingImages ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Fixing Images...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Sparkles className="h-4 w-4" />
                      Fix All Broken Images
                    </span>
                  )}
                </Button>
              </div>

              <div className="border-t pt-6">
                <h4 className="font-medium mb-4">Image Best Practices</h4>
                <div className="grid gap-4 text-sm">
                  <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                    <Check className="h-5 w-5 text-green-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-green-800">Use External URLs</p>
                      <p className="text-green-700">Paste URLs from Pexels, Unsplash, or your CDN for reliable images</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <Sparkles className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-blue-800">Use Auto Image</p>
                      <p className="text-blue-700">Click the sparkle button when adding products to auto-assign category-appropriate images</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <Trash2 className="h-5 w-5 text-amber-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800">Avoid Local Uploads</p>
                      <p className="text-amber-700">Local uploads won&apos;t persist in production. Always use external URLs.</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Shipping Settings */}
        <TabsContent value="shipping">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Truck className="h-5 w-5" />
                Shipping Settings
              </CardTitle>
              <CardDescription>Configure shipping rates and free shipping threshold</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                <div>
                  <Label className="text-base font-medium">Enable Shipping Charges</Label>
                  <p className="text-sm text-muted-foreground">When disabled, all orders ship free</p>
                </div>
                <Switch
                  checked={settings.shipping_enabled}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, shipping_enabled: checked }))}
                />
              </div>
              
              {settings.shipping_enabled && (
                <>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="shipping_rate">Standard Shipping Rate ($)</Label>
                      <Input
                        id="shipping_rate"
                        type="number"
                        step="0.01"
                        min="0"
                        value={settings.shipping_rate}
                        onChange={(e) => setSettings(prev => ({ ...prev, shipping_rate: parseFloat(e.target.value) || 0 }))}
                        placeholder="3.99"
                      />
                      <p className="text-xs text-muted-foreground">Charged for orders below the free shipping threshold</p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="free_shipping_threshold">Free Shipping Threshold ($)</Label>
                      <Input
                        id="free_shipping_threshold"
                        type="number"
                        step="0.01"
                        min="0"
                        value={settings.free_shipping_threshold}
                        onChange={(e) => setSettings(prev => ({ ...prev, free_shipping_threshold: parseFloat(e.target.value) || 0 }))}
                        placeholder="50.00"
                      />
                      <p className="text-xs text-muted-foreground">Orders at or above this amount ship free</p>
                    </div>
                  </div>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-medium text-blue-800 mb-2">Current Shipping Policy</h4>
                    <ul className="text-sm text-blue-700 space-y-1">
                      <li>• Orders under ${settings.free_shipping_threshold?.toFixed(2) || '50.00'}: <strong>${settings.shipping_rate?.toFixed(2) || '3.99'}</strong> shipping</li>
                      <li>• Orders of ${settings.free_shipping_threshold?.toFixed(2) || '50.00'} or more: <strong>FREE</strong> shipping</li>
                    </ul>
                  </div>
                </>
              )}
              
              <Button onClick={handleSaveSettings} className="btn-primary" disabled={saving}>
                {saving ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Saving...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Save className="h-4 w-4" />
                    Save Shipping Settings
                  </span>
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Book Subcategories */}
        <TabsContent value="books">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="h-5 w-5" />
                Book Subcategories
              </CardTitle>
              <CardDescription>Manage subcategories that appear in the Books dropdown menu</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2 mb-6">
                <Input
                  value={newSubcategory}
                  onChange={(e) => setNewSubcategory(e.target.value)}
                  placeholder="New subcategory name"
                  onKeyDown={(e) => e.key === 'Enter' && handleAddSubcategory()}
                />
                <Button onClick={handleAddSubcategory} className="btn-accent">
                  <Plus className="h-4 w-4 mr-2" />
                  Add
                </Button>
              </div>
              <div className="space-y-2">
                {bookSubcategories.map((subcat) => (
                  <div key={subcat.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-md">
                    <span className="font-medium">{subcat.name}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => handleDeleteSubcategory(subcat.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                {bookSubcategories.length === 0 && (
                  <p className="text-muted-foreground text-center py-4">No subcategories yet</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Admin Users Management */}
        <TabsContent value="admins">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Admin Invites
              </CardTitle>
              <CardDescription>
                Create invite codes to add new administrators. Share the code with the new admin - 
                they can register at <code className="bg-muted px-1 rounded">/admin/setup</code>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2 mb-6">
                <Input
                  type="email"
                  value={newInviteEmail}
                  onChange={(e) => setNewInviteEmail(e.target.value)}
                  placeholder="New admin's email address"
                  onKeyDown={(e) => e.key === 'Enter' && handleCreateInvite()}
                />
                <Button onClick={handleCreateInvite} className="btn-accent">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Invite
                </Button>
              </div>
              <div className="space-y-3">
                {adminInvites.map((invite) => (
                  <div key={invite.id} className={`p-4 rounded-lg border ${invite.used ? 'bg-muted/30' : 'bg-card'}`}>
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <p className="font-medium">{invite.email}</p>
                        <div className="flex items-center gap-2">
                          <code className="text-lg font-mono bg-primary/10 text-primary px-3 py-1 rounded">
                            {invite.invite_code}
                          </code>
                          {!invite.used && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => copyInviteCode(invite.invite_code)}
                            >
                              {copiedCode === invite.invite_code ? (
                                <Check className="h-4 w-4 text-green-500" />
                              ) : (
                                <Copy className="h-4 w-4" />
                              )}
                            </Button>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Created {new Date(invite.created_at).toLocaleDateString()}
                          {invite.used && <span className="ml-2 text-green-600 font-medium">• Used</span>}
                        </p>
                      </div>
                      {!invite.used && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => handleDeleteInvite(invite.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
                {adminInvites.length === 0 && (
                  <p className="text-muted-foreground text-center py-8">
                    No invites yet. Create one to add a new administrator.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Subscribers */}
        <TabsContent value="subscribers">
          <Card>
            <CardHeader>
              <CardTitle>Newsletter Subscribers</CardTitle>
              <CardDescription>People subscribed to your newsletter</CardDescription>
            </CardHeader>
            <CardContent>
              {subscribers.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No subscribers yet</p>
              ) : (
                <div className="space-y-2">
                  {subscribers.map((subscriber) => (
                    <div key={subscriber.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-md">
                      <div>
                        <span className="font-medium">{subscriber.email}</span>
                        <span className="text-xs text-muted-foreground ml-2">
                          {new Date(subscriber.subscribed_at).toLocaleDateString()}
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => handleDeleteSubscriber(subscriber.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminSettings;
